import { Location } from '@angular/common';
import { Directive, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { Status } from '@df/core/status';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { GtmService } from '@df/module-gtm/gtm.service';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { LoginFormComponentDef } from '@df/session/login/login-form.component.def';
import { Login } from '@df/session/login/login.interfaces';
import { ExponeaService } from '../../../module/exponea/exponea.service';
import { NLoginViewLayout } from '../../view/login-view-layout.interface';

declare module '@df/session/login/login.interfaces' {
    export namespace Login {
        export interface ILoginFormData {
            newsletter?: boolean;
        }

        export interface IResetPasswordFormData {
            newsletter?: boolean;
        }
    }
}

@Directive()
export abstract class LoginFormContentAbstract extends LoginFormComponentDef implements OnInit, OnDestroy {
    @Input()
    cmsBlock!: NLoginViewLayout.IContentData;

    isLoginForm = true;
    isRegisterForm = false;
    exponeaConsentResult = false;
    exponeaConsentStatus = new Status();
    protected returnUrl?: string;
    protected newsletterPlacement = 'login';
    protected scrollToService = inject(ScrollToService);
    protected gtmService = inject(GtmService);
    protected router = inject(Router);
    protected sessionStorageService = inject(SessionStorageService);
    private exponeaService = inject(ExponeaService);
    private location = inject(Location);
    protected readonly timeoutService = injectTimeoutService();

    get showSubscriptionOptIn(): boolean {
        return !this.exponeaConsentResult && !this.exponeaConsentStatus.busy;
    }

    get isNewUser(): boolean {
        return super.shouldRegister();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.returnUrl = this.sessionStorageService.get('returnUrl') || '/account';

        this.subscriptions.add(this.exponeaConsentStatus.subscribe(() => this.detectChanges()));
        this.subscriptions.add(this.checkStatus.subscribe(() => this.detectChanges()));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.returnUrl = undefined;
    }

    showLoginForm(value = true): void {
        if (this.form) {
            this.form['submitAttempt'] = false;
        }
        this.isLoginForm = value;
        this.isRegisterForm = !value;

        this.detectChanges();
    }

    showRegisterForm(value = true): void {
        if (this.form) {
            this.form['submitAttempt'] = false;
        }
        this.isLoginForm = !value;
        this.isRegisterForm = value;

        this.detectChanges();
    }

    close(): void {
        this.location.go('/');
    }

    submitLogin(): void {
        if (this.isRegisterForm) {
            this.isRegisterForm = false;
            this.detectChanges();
        }

        if (this.form?.valid) {
            this.setPlacement();
            const onSuccess = () => this.onSubmitSuccess();
            this.login(onSuccess);
        } else {
            if (this.form) {
                this.form['submitAttempt'] = true;
                this.form.ngSubmit.emit();
            }
            this.detectChanges();
        }
    }

    submitRegister(): void {
        if (this.isLoginForm) {
            this.isLoginForm = false;
            this.detectChanges();
        }

        if (this.form?.valid) {
            this.setPlacement();
            const onSuccess = () => this.onSubmitSuccess();
            this.register(onSuccess);
        } else {
            if (this.form) {
                this.form['submitAttempt'] = true;
                this.form.ngSubmit.emit();
            }
            this.detectChanges();
        }
    }

    /**
     * Sets placement param to data, it is passed trough to Exponea and GTM events,
     * 'placement' and 'newsletter' params are removed from data within exponea.service.ts before data is send.
     */
    private setPlacement(): void {
        if (this.data.newsletter && this.newsletterPlacement) {
            this.data['placement'] = this.newsletterPlacement;
        }
    }

    protected override onEmailChange(newEmail: string | undefined, oldEmail: string | undefined): void {
        this.checkExponeaConsent(this.email);

        super.onEmailChange(newEmail, oldEmail);
    }

    protected override setCheckResult(checkResult?: Login.IResponseAccountExists) {
        if (checkResult) {
            if (checkResult.exists) {
                this.isLoginForm = checkResult.exists;

                if (this.isLoginForm && this.isRegisterForm) {
                    this.isRegisterForm = false;

                    if (this.app.client.isS) {
                        this.timeoutService.setTimeout(() => this.scrollToService.scrollTo('#login-form', { offset: 50 }), 100);
                    }
                }
            } else {
                this.isLoginForm = false;
                this.isRegisterForm = true;
            }
        }

        super.setCheckResult(checkResult);
    }

    protected onSubmitSuccess(): void {
        this.timeoutService.setTimeout(() => {
            if (this.returnUrl) {
                this.router.navigateByUrl(this.returnUrl);
            } else {
                this.router.navigateByUrl('/account');
            }

            this.detectChanges();
        }, 100);
    }

    protected checkExponeaConsent(email?: string): void {
        if (!email) {
            return;
        }

        this.exponeaService.checkConsent(this.exponeaConsentStatus, email, status => {
            this.exponeaConsentResult = status.consent;
            this.detectChanges();
        });
    }

    dataChangeHandle(newsletter: boolean): void {
        this.data.newsletter = newsletter;

        this.detectChanges();
    }
}
